/**
 * ProseMirror node definition and schema builder for atomic template variables
 * in the ngx-editor WYSIWYG editor.
 */
import { NodeSpec, Schema } from 'prosemirror-model';
import { nodes as defaultNodes, marks as defaultMarks } from 'ngx-editor/schema';
import { TemplateVariable, buildVariablePattern } from './template-variable.utils';

/**
 * Creates a ProseMirror NodeSpec for template variables.
 * The node is atomic (non-editable, single-backspace-delete, arrow-key-skip).
 * Closes over the variables array to populate label/description during parsing.
 */
export function createTemplateVariableNodeSpec(variables: TemplateVariable[]): NodeSpec {
    const variableMap = new Map<string, TemplateVariable>();
    variables.forEach(v => variableMap.set(v.value, v));

    return {
        group: 'inline',
        inline: true,
        atom: true,
        selectable: false,
        draggable: false,
        attrs: {
            value: {},
            label: { default: null },
            description: { default: null },
        },
        toDOM(node: any) {
            return ['span', {
                class: 'template-variable-tag',
                'data-template-variable': node.attrs.value,
                contenteditable: 'false',
                title: node.attrs.description || '',
            }, node.attrs.value];
        },
        parseDOM: [{
            tag: 'span[data-template-variable]',
            getAttrs(dom: any) {
                const value = dom.getAttribute('data-template-variable') || '';
                const info = variableMap.get(value);
                return {
                    value,
                    label: info?.label || null,
                    description: info?.description || null,
                };
            },
        }],
        leafText(node: any) {
            return node.attrs.value;
        },
    };
}

/**
 * Builds a ProseMirror Schema that extends ngx-editor's default schema
 * with a `template_variable` inline atom node.
 */
export function buildTemplateVariableSchema(variables: TemplateVariable[]): Schema {
    const extendedNodes: Record<string, NodeSpec> = {
        ...(defaultNodes as any),
        template_variable: createTemplateVariableNodeSpec(variables),
    };
    return new Schema({ nodes: extendedNodes, marks: defaultMarks as any });
}

/**
 * Pre-processes HTML for ProseMirror parsing by wrapping plain-text
 * variable tokens in bridge `<span data-template-variable>` elements
 * that the custom node's `parseDOM` can match.
 *
 * Uses DOM-based text node walking to avoid replacing tokens that
 * appear inside HTML attributes (e.g. `href="{SIGNUPURL}"`).
 */
export function preprocessHtmlForEditor(html: string, variables: TemplateVariable[]): string {
    if (!html || variables.length === 0) return html;

    const pattern = buildVariablePattern(variables);
    if (!pattern) return html;

    const div = document.createElement('div');
    div.innerHTML = html;

    const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    while (walker.nextNode()) {
        textNodes.push(walker.currentNode as Text);
    }

    for (const textNode of textNodes) {
        const text = textNode.textContent || '';
        pattern.lastIndex = 0;
        if (!pattern.test(text)) continue;
        pattern.lastIndex = 0;

        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = pattern.exec(text)) !== null) {
            if (match.index > lastIndex) {
                fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
            }
            const span = document.createElement('span');
            span.setAttribute('data-template-variable', match[0]);
            fragment.appendChild(span);
            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < text.length) {
            fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        }

        textNode.parentNode?.replaceChild(fragment, textNode);
    }

    return div.innerHTML;
}
